import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Star, Gift, ArrowLeft, Send, Clock, CheckCircle } from "lucide-react";

const CASHBACK_AMOUNT = 2;

const ReviewPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Form state
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [yappyNumber, setYappyNumber] = useState("");
  const [hoveredStar, setHoveredStar] = useState(0);

  const cashbackAmount = invitation?.cashback_amount || CASHBACK_AMOUNT;

  useEffect(() => {
    if (user && productId) {
      fetchInvitation();
    }
  }, [user, productId]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`${API}/reviews/my-invitations`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      const inv = response.data.find(i => i.product_id === productId);
      if (inv) {
        setInvitation(inv);
      }
    } catch (error) {
      console.error("Error fetching invitation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error("Por favor selecciona una calificación");
      return;
    }
    if (!comment.trim()) {
      toast.error("Por favor escribe un comentario");
      return;
    }
    if (!yappyNumber.trim()) {
      toast.error("Por favor ingresa tu número de Yappy para recibir el cashback");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId,
        rating,
        comment: comment.trim(),
        yappy_number: yappyNumber.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      setSubmitted(true);
      toast.success("¡Review enviado! Recibirás tu cashback pronto 🎉");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar review");
    } finally {
      setSubmitting(false);
    }
  };

  const getRemainingTime = () => {
    if (!invitation?.deadline) return null;
    const deadline = new Date(invitation.deadline);
    const now = new Date();
    const diff = deadline - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days} días restantes`;
    if (hours > 0) return `${hours} horas restantes`;
    return "Expira pronto";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
        <Header />
        <main className="max-w-lg mx-auto px-4 py-8 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E2E8F0]">
            <Gift size={64} className="mx-auto text-[#CBD5E1] mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Sin invitación</h2>
            <p className="text-[#64748B] mb-4">
              No tienes una invitación pendiente para dejar review de este producto.
            </p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              Volver al inicio
            </Button>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
        <Header />
        <main className="max-w-lg mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-[#E2E8F0] text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#10B981]/10 flex items-center justify-center">
              <CheckCircle size={40} className="text-[#10B981]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0F172A] mb-2">¡Gracias por tu review!</h2>
            <p className="text-[#64748B] mb-6">
              Tu cashback de <span className="font-bold text-[#10B981]">${cashbackAmount}</span> será enviado a tu Yappy pronto.
            </p>
            <div className="p-4 bg-[#F1F5F9] rounded-xl mb-6">
              <p className="text-sm text-[#64748B]">Número de Yappy registrado:</p>
              <p className="font-bold text-[#0F172A]">{yappyNumber}</p>
            </div>
            <Button onClick={() => navigate("/")} className="rounded-full w-full h-12 bg-[#475569] hover:bg-[#334155]">
              Volver al inicio
            </Button>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />
      
      <main className="max-w-lg mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        {/* Cashback Banner */}
        <div className="bg-gradient-to-r from-[#10B981] to-[#059669] rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Gift size={32} />
            <div>
              <h2 className="text-xl font-bold">¡Gana ${cashbackAmount} de Cashback!</h2>
              <p className="text-white/80 text-sm">Deja tu review y recibe el dinero en tu Yappy</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 text-white/90 text-sm">
            <Clock size={16} />
            <span>{getRemainingTime()}</span>
          </div>
        </div>

        {/* Product Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-[#E2E8F0] mb-6">
          <div className="flex items-center gap-4">
            <img
              src={invitation.product_image}
              alt={invitation.product_title}
              className="w-20 h-20 object-cover rounded-xl"
            />
            <div>
              <h3 className="font-semibold text-[#0F172A]">{invitation.product_title}</h3>
              <p className="text-sm text-[#64748B]">Comprado por ${invitation.product_price}</p>
            </div>
          </div>
        </div>

        {/* Review Form */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#E2E8F0] space-y-6">
          {/* Rating */}
          <div className="space-y-3">
            <Label className="text-[#0F172A] font-medium">¿Cómo calificas tu compra?</Label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      star <= (hoveredStar || rating)
                        ? "fill-[#F59E0B] text-[#F59E0B]"
                        : "text-[#E2E8F0]"
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-[#64748B]">
              {rating === 1 && "Muy malo 😞"}
              {rating === 2 && "Malo 😕"}
              {rating === 3 && "Regular 😐"}
              {rating === 4 && "Bueno 🙂"}
              {rating === 5 && "Excelente 🤩"}
            </p>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-[#0F172A] font-medium">
              Cuéntanos tu experiencia
            </Label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="¿Qué te pareció el producto? ¿Cómo fue la entrega? ¿Lo recomendarías?"
              className="min-h-[120px] rounded-xl resize-none"
              data-testid="review-comment"
            />
          </div>

          {/* Yappy Number */}
          <div className="space-y-2">
            <Label htmlFor="yappy" className="text-[#0F172A] font-medium flex items-center gap-2">
              <img 
                src="https://www.yappy.com.pa/images/logo-yappy.svg" 
                alt="Yappy" 
                className="h-5"
                onError={(e) => e.target.style.display = 'none'}
              />
              Número de Yappy para recibir tu cashback
            </Label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B] font-medium">
                +507
              </span>
              <Input
                id="yappy"
                value={yappyNumber}
                onChange={(e) => setYappyNumber(e.target.value.replace(/[^0-9-]/g, ""))}
                placeholder="6123-4567"
                className="pl-16 rounded-xl h-12"
                data-testid="yappy-number"
              />
            </div>
            <p className="text-xs text-[#64748B]">
              Ingresa el número asociado a tu cuenta Yappy
            </p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || !rating || !comment.trim() || !yappyNumber.trim()}
            className="w-full h-14 rounded-full bg-[#10B981] hover:bg-[#059669] text-lg font-semibold"
            data-testid="submit-review"
          >
            {submitting ? (
              <div className="spinner" style={{ width: 24, height: 24 }} />
            ) : (
              <>
                <Send size={20} className="mr-2" />
                Enviar Review y Recibir ${cashbackAmount}
              </>
            )}
          </Button>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};

export default ReviewPage;
